import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

function StatCard({ title, value, subtitle, color = "#10b981", icon, trend }) {
  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 28, fontWeight: 900, color }}>{value}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{subtitle}</div>}
        </div>
        {icon && <div style={{ fontSize: 32, opacity: 0.5 }}>{icon}</div>}
      </div>
    </div>
  );
}

export default function SustainabilityDashboard() {
  const { adminEmail } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carrierRankings, setCarrierRankings] = useState([]);
  const [laneEmissions, setLaneEmissions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [form, setForm] = useState({ distanceMiles: 500, weightLbs: 40000, equipmentType: "Dry Van", fuelType: "DIESEL" });
  const [calcResult, setCalcResult] = useState(null);

  const headers = { 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' };

  useEffect(() => { loadDashboard(); }, []);

  async function loadDashboard() {
    setLoading(true);
    try {
      const [dashRes, carriersRes, lanesRes] = await Promise.all([
        fetch("/api/sustainability/dashboard", { headers }).then(r => r.json()),
        fetch("/api/sustainability/carrier-rankings", { headers }).then(r => r.json()).catch(() => ({ carriers: [] })),
        fetch("/api/sustainability/lane-emissions", { headers }).then(r => r.json()).catch(() => ({ lanes: [] }))
      ]);
      setDashboard(dashRes);
      setCarrierRankings(carriersRes.carriers || []);
      setLaneEmissions(lanesRes.lanes || []);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  }

  async function loadRecommendations() {
    setLoadingRecs(true);
    try {
      const res = await fetch("/api/sustainability/recommendations", { headers });
      const data = await res.json();
      setRecommendations(data.recommendations || []);
    } catch (e) { console.error('Recommendations error:', e); }
    finally { setLoadingRecs(false); }
  }

  const calculate = async () => {
    const res = await fetch("/api/sustainability/calculate", { 
      method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(form) 
    });
    const data = await res.json();
    setCalcResult(data.calculation);
    loadDashboard();
  };

  async function downloadESGReport() {
    try {
      const res = await fetch("/api/sustainability/esg-report", { headers });
      const data = await res.json();
      const report = `
╔══════════════════════════════════════════════════════════════╗
║           ESG SUSTAINABILITY REPORT - ROADOPTIX              ║
╚══════════════════════════════════════════════════════════════╝

Generated: ${new Date().toLocaleString()}
Period: ${data.period || 'Year to Date'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 ENVIRONMENTAL METRICS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Total CO₂ Emissions:     ${data.environmental?.totalCO2Tons || 0} metric tons
Total Distance:          ${(data.environmental?.totalMiles || 0).toLocaleString()} miles
Trees Needed to Offset:  ${data.environmental?.treesNeededToOffset || 0} trees
Estimated Offset Cost:   $${(data.environmental?.estimatedOffsetCost || 0).toLocaleString()}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
👥 SOCIAL METRICS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Carrier Partners:        ${data.social?.totalCarrierPartners || 0}
Customers Served:        ${data.social?.totalCustomers || 0}
Shipments Completed:     ${data.social?.shipmentsCompleted || 0}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚖️ GOVERNANCE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Compliance Rate:         ${data.governance?.complianceRate || 100}%
Audit Status:            ${data.governance?.auditStatus || 'Compliant'}
Certifications:          ${(data.governance?.certifications || []).join(', ') || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

© ${new Date().getFullYear()} RoadOptix - Symple Code Business Systems
This report was automatically generated for ESG compliance purposes.
      `;
      const blob = new Blob([report], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RoadOptix_ESG_Report_${new Date().toISOString().split("T")[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Error generating report: " + e.message);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading sustainability data...</div>;

  const summary = dashboard?.summary || dashboard || {};
  const totalCO2 = parseFloat(summary.totalCO2Kg || 0);
  const totalMiles = parseInt(summary.totalMiles || 0);
  const avgEmissionsPerMile = totalMiles > 0 ? (totalCO2 / totalMiles).toFixed(3) : 0;
  const treesNeeded = Math.ceil(totalCO2 / 21.77);

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: "0 0 8px 0", fontSize: 28, fontWeight: 900 }}>🌱 Sustainability Dashboard</h1>
          <p style={{ margin: 0, color: "#6b7280" }}>Track CO₂ emissions, ESG metrics, and environmental impact</p>
        </div>
        <button onClick={loadDashboard} style={{ padding: "8px 16px", background: "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>🔄 Refresh</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, overflowX: 'auto', paddingBottom: 8 }}>
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'carriers', label: '🚛 Carrier Rankings' },
          { id: 'lanes', label: '🛣️ Lane Analysis' },
          { id: 'recommendations', label: '💡 AI Recommendations' },
          { id: 'calculator', label: '🧮 Calculator' },
          { id: 'report', label: '📄 ESG Report' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '8px 16px', background: activeTab === tab.id ? '#10b981' : 'white',
            color: activeTab === tab.id ? 'white' : '#6b7280', border: '1px solid #e5e7eb',
            borderRadius: 8, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 13
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <StatCard title="Total CO₂ Emissions" value={`${(totalCO2 / 1000).toFixed(1)} tons`} subtitle={`${summary.totalLoads || 0} loads tracked`} icon="🌍" color="#059669"/>
            <StatCard title="Total Miles" value={totalMiles.toLocaleString()} subtitle="Distance traveled" icon="🛣️" color="#3b82f6"/>
            <StatCard title="Avg Emissions/Mile" value={`${avgEmissionsPerMile} kg`} subtitle="CO₂ per mile" icon="📊" color="#8b5cf6"/>
            <StatCard title="Trees to Offset" value={treesNeeded.toLocaleString()} subtitle="Annual equivalent" icon="🌳" color="#10b981"/>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
            <div style={{ background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)', borderRadius: 16, padding: 24, color: 'white' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 20 }}>🌍 Environmental Impact Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <div><div style={{ fontSize: 32, fontWeight: 900 }}>{(totalCO2 / 1000).toFixed(1)}</div><div style={{ opacity: 0.8, fontSize: 13 }}>Metric Tons CO₂</div></div>
                <div><div style={{ fontSize: 32, fontWeight: 900 }}>{treesNeeded}</div><div style={{ opacity: 0.8, fontSize: 13 }}>Trees Needed</div></div>
                <div><div style={{ fontSize: 32, fontWeight: 900 }}>${(treesNeeded * 15).toLocaleString()}</div><div style={{ opacity: 0.8, fontSize: 13 }}>Est. Offset Cost</div></div>
              </div>
              <div style={{ marginTop: 20, padding: 12, background: 'rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 13 }}>
                💡 <strong>Tip:</strong> Switching 10% of your fleet to electric vehicles could reduce emissions by ~{(totalCO2 * 0.1 / 1000).toFixed(1)} tons annually.
              </div>
            </div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>🎯 Quick Actions</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <button onClick={() => setActiveTab('recommendations')} style={{ padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, color: '#065f46' }}>💡 Get AI Recommendations</div>
                  <div style={{ fontSize: 12, color: '#047857' }}>Reduce your carbon footprint</div>
                </button>
                <button onClick={() => setActiveTab('report')} style={{ padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, color: '#1e40af' }}>📄 Generate ESG Report</div>
                  <div style={{ fontSize: 12, color: '#3b82f6' }}>For stakeholders & compliance</div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Carrier Rankings Tab */}
      {activeTab === 'carriers' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: 0 }}>🚛 Carrier Sustainability Rankings</h3>
          </div>
          {carrierRankings.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No carrier data available yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Rank</th>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Carrier</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Loads</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Miles</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>CO₂ (kg)</th>
                <th style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>Grade</th>
              </tr></thead>
              <tbody>
                {carrierRankings.map((c, i) => {
                  const grade = c.emissionsPerMile < 1.0 ? 'A' : c.emissionsPerMile < 1.2 ? 'B' : c.emissionsPerMile < 1.5 ? 'C' : 'D';
                  const colors = { A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#ef4444' };
                  return (
                    <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 12 }}>{i < 3 ? ['🥇','🥈','🥉'][i] : `#${i+1}`}</td>
                      <td style={{ padding: 12, fontWeight: 600 }}>{c.name}</td>
                      <td style={{ padding: 12, textAlign: 'right' }}>{c.loadCount}</td>
                      <td style={{ padding: 12, textAlign: 'right' }}>{parseInt(c.totalMiles || 0).toLocaleString()}</td>
                      <td style={{ padding: 12, textAlign: 'right' }}>{parseFloat(c.totalCO2 || 0).toFixed(1)}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}><span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: '50%', background: colors[grade], color: 'white', fontWeight: 900, lineHeight: '28px' }}>{grade}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Lane Analysis Tab */}
      {activeTab === 'lanes' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}><h3 style={{ margin: 0 }}>🛣️ Lane Emissions Analysis</h3></div>
          {laneEmissions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No lane data available yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Lane</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Loads</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Avg Miles</th>
                <th style={{ padding: 12, textAlign: 'right', fontSize: 12, color: '#6b7280' }}>Total CO₂</th>
              </tr></thead>
              <tbody>
                {laneEmissions.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{l.origin}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{l.loadCount}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{parseInt(l.avgMiles || 0).toLocaleString()}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{parseFloat(l.totalCO2 || 0).toFixed(1)} kg</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* AI Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>💡 AI Sustainability Recommendations</h2>
            <button onClick={loadRecommendations} disabled={loadingRecs} style={{ padding: '8px 16px', background: loadingRecs ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: loadingRecs ? 'not-allowed' : 'pointer' }}>
              {loadingRecs ? '🧠 Analyzing...' : '🧠 Generate'}
            </button>
          </div>
          {recommendations.length === 0 ? (
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🌱</div>
              <div style={{ fontWeight: 700, color: '#065f46' }}>Get Personalized Recommendations</div>
              <div style={{ color: '#047857', fontSize: 14 }}>Click "Generate" to get AI-powered suggestions.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {recommendations.map((rec, i) => (
                <div key={i} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ fontSize: 32 }}>{rec.icon || '💡'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>{rec.title}</div>
                      <div style={{ color: '#4b5563', fontSize: 14, marginBottom: 12 }}>{rec.description}</div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
                        <span style={{ color: '#10b981' }}>💰 {rec.savings}</span>
                        <span style={{ color: '#3b82f6' }}>🌍 {rec.co2Reduction}</span>
                        <span style={{ color: '#8b5cf6' }}>⏱️ {rec.timeline}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calculator Tab */}
      {activeTab === 'calculator' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px 0' }}>🧮 Emissions Calculator</h3>
            <div style={{ display: 'grid', gap: 16 }}>
              <div><label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Distance (miles)</label>
                <input type="number" value={form.distanceMiles} onChange={e => setForm({ ...form, distanceMiles: parseInt(e.target.value) || 0 })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}/></div>
              <div><label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Weight (lbs)</label>
                <input type="number" value={form.weightLbs} onChange={e => setForm({ ...form, weightLbs: parseInt(e.target.value) || 0 })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}/></div>
              <div><label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Equipment</label>
                <select value={form.equipmentType} onChange={e => setForm({ ...form, equipmentType: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <option>Dry Van</option><option>Reefer</option><option>Flatbed</option><option>Tanker</option>
                </select></div>
              <div><label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Fuel Type</label>
                <select value={form.fuelType} onChange={e => setForm({ ...form, fuelType: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <option value="DIESEL">Diesel</option><option value="CNG">CNG</option><option value="ELECTRIC">Electric</option>
                </select></div>
              <button onClick={calculate} style={{ padding: 12, background: '#10b981', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Calculate</button>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)', borderRadius: 16, padding: 24, color: 'white' }}>
            <h3 style={{ margin: '0 0 20px 0' }}>📊 Results</h3>
            {calcResult ? (
              <div style={{ display: 'grid', gap: 16 }}>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>CO₂ Emissions</div>
                  <div style={{ fontSize: 32, fontWeight: 900 }}>{calcResult.co2Kg?.toFixed(1)} kg</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Fuel Consumed</div>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>{calcResult.fuelGallons?.toFixed(1)} gallons</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 12 }}>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>Trees to Offset</div>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>{Math.ceil((calcResult.co2Kg || 0) / 21.77)} trees/year</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, opacity: 0.7 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🧮</div>
                <div>Enter values and click Calculate</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ESG Report Tab */}
      {activeTab === 'report' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 8px 0' }}>📄 ESG Report Generator</h3>
          <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>Generate environmental, social, and governance reports</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#ecfdf5', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌍</div>
              <div style={{ fontWeight: 700, color: '#065f46' }}>Environmental</div>
              <div style={{ fontSize: 13, color: '#047857', marginTop: 4 }}>{(totalCO2 / 1000).toFixed(1)} tons CO₂</div>
            </div>
            <div style={{ background: '#eff6ff', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div style={{ fontWeight: 700, color: '#1e40af' }}>Social</div>
              <div style={{ fontSize: 13, color: '#3b82f6', marginTop: 4 }}>{carrierRankings.length} carrier partners</div>
            </div>
            <div style={{ background: '#fef3c7', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚖️</div>
              <div style={{ fontWeight: 700, color: '#92400e' }}>Governance</div>
              <div style={{ fontSize: 13, color: '#d97706', marginTop: 4 }}>100% compliant</div>
            </div>
          </div>

          <div style={{ background: '#f9fafb', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Report Summary</h4>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#4b5563' }}>
              <li>Total tracked shipments: {summary.totalLoads || 0}</li>
              <li>Total distance: {totalMiles.toLocaleString()} miles</li>
              <li>Carbon footprint: {(totalCO2 / 1000).toFixed(2)} metric tons CO₂e</li>
              <li>Estimated offset cost: ${(treesNeeded * 15).toLocaleString()}</li>
              <li>Recommended action: Plant {treesNeeded} trees or purchase carbon credits</li>
            </ul>
          </div>

          <button onClick={downloadESGReport} style={{ padding: '12px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
            📥 Download Full ESG Report
          </button>
        </div>
      )}
    </div>
  );
}
